<template>
  <div :class="$style.base">
    <Header
      v-bind="{ steps, currentStep, currentStepIndex }"
      :class="$style.header"
      @back="prevStep"
    />
    <Alerts
      v-if="
        currentStep.name !== 'booking-completed' &&
        widgetSettings.type === 'booking'
      "
    />
    <div
      :class="[
        $style.main,
        {
          [$style.overflowHidden]: currentStep.name === 'choose-services'
        }
      ]"
    >
      <transition :name="transitionName">
        <component
          :is="currentStep.component"
          :class="$style.page"
          @submit="nextStep"
          @back="prevStep"
          @scroll="onScroll"
        />
      </transition>
      <transition>
        <RequestAppointmentOverviewBanner
          v-show="!hasScrolled"
          :class="$style.banner"
        />
      </transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Step } from '../../types';

import { computed, inject, ref, shallowRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useCompanyStore } from '@/stores/company';
import widgetSettings from '@/widget-settings';

import { useBookingStore } from '@/stores/booking';
import { useEmployeesStore } from '@/stores/employees';
import Alerts from './components/Alerts.vue';
import Header from './components/Header.vue';
import RequestAppointmentOverviewBanner from './components/RequestAppointmentOverviewBanner.vue';
import AppointmentRequest from './appointment-request/index.vue';
import BookingCompleted from './booking-completed/index.vue';
import ChooseDate from './choose-date/index.vue';
import ChooseEmployee from './choose-employee/index.vue';
import ChooseGiftcard from './choose-giftcard/index.vue';
import ChooseLocation from './choose-location/index.vue';
import ChooseServices from './choose-services/index.vue';
import SaveBooking from './save-booking/index.vue';

defineOptions({ name: 'Steps' });
const mixpanel = inject<any>('mixpanel');
const { t } = useI18n();
const { settings, employeeSelectionRequired, requestBooking } =
  useCompanyStore();

const steps = ref<Step[]>(
  [
    {
      name: 'appointment-request',
      component: shallowRef(AppointmentRequest),
      show:
        requestBooking &&
        !widgetSettings.bookingId &&
        widgetSettings.type === 'booking',
      heading: computed(() => t('appointment_request.heading')),
      trackingPath: ''
    },
    {
      name: 'choose-location',
      component: shallowRef(ChooseLocation),
      show:
        settings.multi_location &&
        !widgetSettings.bookingId &&
        widgetSettings.type === 'booking',
      heading: computed(() => t('location.heading')),
      trackingPath: '/location'
    },
    {
      name: 'choose-employee-first',
      component: shallowRef(ChooseEmployee),
      show:
        settings.employee_selection_first &&
        !widgetSettings.bookingId &&
        widgetSettings.type === 'booking',
      heading: computed(() => t('resource.heading')),
      trackingPath: '/resource'
    },
    {
      name: 'choose-services',
      component: shallowRef(ChooseServices),
      show: !widgetSettings.bookingId && widgetSettings.type === 'booking',
      heading: computed(() => t('services.heading')),
      trackingPath: '/services'
    },
    {
      name: 'choose-giftcard',
      component: shallowRef(ChooseGiftcard),
      show: widgetSettings.type === 'giftcard',
      heading: computed(() => t('voucher.heading')),
      trackingPath: '/choose-amount'
    },
    {
      name: 'choose-employee',
      component: shallowRef(ChooseEmployee),
      show:
        employeeSelectionRequired &&
        !settings.employee_selection_first &&
        !widgetSettings.bookingId &&
        widgetSettings.type === 'booking',
      heading: computed(() => t('resource.heading')),
      trackingPath: ''
    },
    {
      name: 'choose-date',
      component: shallowRef(ChooseDate),
      show: widgetSettings.type === 'booking',
      heading: computed(() => t('availabilities.heading')),
      trackingPath: '/appointment'
    },
    {
      name: 'confirm-booking',
      component: shallowRef(SaveBooking),
      show: !widgetSettings.bookingId,
      heading: computed(() => {
        const { waitingList } = useBookingStore();

        widgetSettings.type === 'giftcard'
          ? t('confirmation.heading_giftcard')
          : waitingList
            ? t('waiting_list.heading')
            : t('confirmation.heading');
      }),
      trackingPath: '/personal-info'
    },
    {
      name: 'booking-completed',
      component: shallowRef(BookingCompleted),
      show: true,
      heading: '',
      trackingPath: '/confirmed'
    }
  ].filter((step) => step.show)
);

const currentStepIndex = ref(0);
const currentStep = computed(() => steps.value[currentStepIndex.value]);

const nextStep = () => {
  if (currentStepIndex.value < steps.value.length - 1) {
    currentStepIndex.value++;
  }
};

const prevStep = () => {
  if (
    (currentStep.value.name === 'choose-date' && employeeSelectionRequired) ||
    (currentStep.value.name === 'choose-services' &&
      settings.employee_selection_first &&
      steps.value[0].name !== 'choose-employee-first')
  ) {
    // When going back from the 'choose-date' step and employee selection is required, you enter the employee selection step
    // Same as when you back from the 'choose-services' step when employee selection is first
    // When this is the case and there is only one employee, or no employees at all, it should skip that step and go to 'choose-services' instead
    const { employees } = useEmployeesStore();
    if (employees.length <= 1) {
      currentStepIndex.value = currentStepIndex.value - 2;
      return;
    }
  }

  if (currentStepIndex.value > 0) {
    currentStepIndex.value--;
  }
};

const transitionName = ref('page-next');
watch(currentStepIndex, (curValue, prevValue) => {
  transitionName.value = curValue < prevValue ? 'page-prev' : 'page-next';
});

const gtag = inject<any>('gtag');

watch(
  currentStep,
  (value) => {
    mixpanel.track(`navigate to ${value.name}`);

    if (value.trackingPath) {
      const trackPath = `/${widgetSettings.type}-widget${value.trackingPath}`;
      gtag.trackPage(trackPath);
    }
  },
  {
    immediate: true
  }
);

const hasScrolled = ref(false);

const onScroll = (e) => {
  hasScrolled.value = e.target.scrollTop > 12;
};
</script>

<style lang="scss" module>
.base {
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative; // Needed for overflow fix of appointment overview banner
  overflow: hidden;
}

.header {
  height: $header-height;
  flex-shrink: 0;
}

.main {
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative; // Needed for the page animation, which uses position: absolute
  -webkit-overflow-scrolling: touch;

  &.overflowHidden {
    overflow: hidden; // Fixes scrollbar when animating appointment overview
  }
}

.banner {
  transition: transform 0.15s ease-out 0.05s;

  &:global(.v-enter-from),
  &:global(.v-leave-to) {
    transform: translateY(100%);
  }

  &:global(.v-leave-active) {
    // Position the banner absolutely when animating out, so it doesn't conflict with the services banner
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
  }
}

.page {
  height: 100%;
  overflow-y: auto;

  &:global(.page-next-enter-active),
  &:global(.page-next-leave-active),
  &:global(.page-prev-enter-active),
  &:global(.page-prev-leave-active) {
    transition:
      transform 0.2s $easing,
      opacity 0.2s $easing;
  }

  &:global(.page-next-leave-to),
  &:global(.page-next-enter-from),
  &:global(.page-prev-leave-to),
  &:global(.page-prev-enter-from) {
    opacity: 0;
  }

  &:global(.page-next-leave-to),
  &:global(.page-prev-enter-from) {
    transform: translateX(-50%);
  }

  &:global(.page-next-enter-from),
  &:global(.page-prev-leave-to) {
    transform: translateX(50%);
  }

  &:global(.page-next-enter-active),
  &:global(.page-prev-enter-active) {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
  }
}
</style>
